import type { SagaReturnType } from 'redux-saga/effects';
import { takeLatest, all, call, put, select, delay } from 'redux-saga/effects';
import { isEmpty, pick, get, isEqual } from 'lodash';
import { parseJSON } from 'src/utils/parseJSON';
import URI from 'urijs';

import { addDays } from 'date-fns';
import { INIT_APP } from 'src/store/view/constants';
import { api } from 'src/api';
import { completedOperators, isSearchRequestDone } from 'src/utils/searchUtils';
import { getNightsRange } from 'src/utils/searchParamsUtils';
import { SEARCH_TIMEOUT, STATUS_INTERVAL, MOSCOW_CITY_ID } from 'src/constants';
import {
  getSearchParamsStartDate,
  getSearchParamsAdults,
  getSearchParamsKidsCount,
  getSearchParamsKidsAgesText,
  getSearchParamsFromCity,
  getSearchParamsFromCountry,
  getSearchParamsToCityEn,
  getSearchParamsToCountry,
  getSearchParamsNights,
} from 'src/store/enqueue/selectors';
import { logInAction } from 'src/store/client/actions';
import {
  convertServerDateToClientDate,
  convertClientDateToServerDate,
  checkDateFormat,
} from 'src/utils/dateUtils';
import { searchTypeSelector, getIsHotelOnly } from 'src/store/view/selectors';
import {
  startSearchAction,
  stopSearchAction,
  updateRequestIdAction,
  setSearcherDateAction,
  setSearcherCarouselDateAction,
  setDepartureAction,
  setSearcherFetchingAction,
  setSearcherFiltersAction,
  updateSearcherParamsAction,
  clearSearcherFiltersAction,
  setSearcherDefaultFiltersAction,
  searchPriceForDateAction,
} from '../actions/searcher';
import { setFromSearchOfferAction } from '../actions/searchOffer';
import { getSearcherDepartures } from '../selectors/departures';
import {
  START_SEARCH,
  STOP_SEARCH,
  SEARCH_WITH_DEFAULT_DEPARTURE,
  SET_SEARCHER_CAROUSEL_DATE,
  SET_SEARCHER_PARAMS,
  SET_FILTERS,
  CLEAR_SEARCHER_FILTERS,
} from '../constants/searcher';

import { updateRoomsAction, setRoomsAction } from '../actions/rooms';
import { updateHotelFiltersAction } from '../reducers/filters';
import {
  getSearcherRequestId,
  getSearcherData,
  getSearcherCarouselDate,
  getSearcherLastUpdated,
  getSearcherDeparture,
  getSearcherFilters,
  getIsSetDefaultFilters,
  getSearcherFreeCancel,
  getSearcherInstantConfirm,
  getSearcherOperatorsFilter,
} from '../selectors/searcher';
import { HOTEL_SEARCHER_REPEAT_DELAY } from '../../constants';
import { getRooms } from '../selectors/rooms';

import { commitSearchParamsAction } from '../actions/searchParams';
import { clearSearcherFiltersActionInSetTab } from '../actions/extraItems';

import { isDateBetweenRange, mergeRoomsOffers } from '../../utils/offersUtils';
import {
  getSearchOfferOperators,
  getSearchOfferFilters,
  getSearchOfferFrom,
  getSearchOfferMeals,
  getSearchOfferLabelFilters,
} from '../selectors/searchOffer';
import {
  getLabelsFilters,
  getMealsFilter,
  getRoomsOperators,
  getSearchedNights,
} from '../selectors/common';
import { getSearchType } from '../selectors/view';

import { SET_SEARCH_TYPE } from '../constants/view';
import { SET_ROOMS, UPDATE_ROOMS } from '../constants/rooms';
import { getHotelId, setHotelLabels, setMetapolicies } from '../hotel';

/**
 * Стартует поиск
 * 0. получаем сразу комнаты, если есть requestId
 * 1. загружаем departures (TODO: наверно можно сразу с бэка прокинуть)
 * 2. запускаем поисковик
 * 3. если с поиска прокинуты фильтры по ТО или экстрасам,
 * ожидаем окончания, находим совпадения и применяем
 */
function* handleInitApp() {
  const requestId = yield select(getSearcherRequestId);
  const isHotelOnly = yield select(getIsHotelOnly);

  if (requestId) {
    const { rooms, labels, metapolicies, filters } = yield fetchRooms(
      requestId,
    );

    yield put(setRoomsAction(rooms));
    yield put(setHotelLabels(labels));
    yield put(setMetapolicies(metapolicies));
    if (isHotelOnly && filters) {
      yield put(updateHotelFiltersAction(filters));
    }
  }

  yield put(startSearchAction());

  /**
   * Применяем фильтры, которые пользователь выбрал
   * на странице поиска
   */
  const searchOfferFilters = yield select(getSearchOfferFilters);
  const searchOfferMeals = yield select(getSearchOfferMeals);
  const searchOfferLabels = yield select(getSearchOfferLabelFilters);
  if (searchOfferMeals || searchOfferFilters) {
    const filters = yield select(getSearcherFilters);

    const existedMeals = yield select(getMealsFilter);
    const mealsFilter = existedMeals?.reduce((acc, curr) => {
      if (searchOfferMeals && searchOfferMeals[curr.id.slice(9, 12)]) {
        return {
          ...acc,
          [curr.id]: true,
        };
      }
      return acc;
    }, {});

    const existedLabels = yield select(getLabelsFilters);
    const labelsFilter = existedLabels?.paymentBenefits.reduce((acc, curr) => {
      if (searchOfferLabels && searchOfferLabels[curr.id.slice(7)]) {
        return {
          ...acc,
          [curr.id]: true,
        };
      }
      return acc;
    }, {});

    yield put(setSearcherDefaultFiltersAction(true));
    yield put(
      setSearcherFiltersAction({
        ...filters,
        ...mealsFilter,
        ...labelsFilter,
        ...searchOfferFilters,
      }),
    );
  }
}

/**
 * Перезапускает поисковик
 */
function* restartSearcher() {
  const rooms = yield select(getRooms);

  if (!isEmpty(rooms)) {
    while (true) {
      yield delay(5000);
      const lastUpdated = yield select(getSearcherLastUpdated);

      // Если прошло 30 мин с последнего обновления и вкладка еще активна
      if (
        lastUpdated + HOTEL_SEARCHER_REPEAT_DELAY - Date.now() <= 0 &&
        !document.hidden
      ) {
        break;
      }
    }

    // TODO: непонятно, что тут делать, по идее надо чистить,
    // чтобы убрать неактуальную выдачу, но тогда все пропадает
    // и юзера может кинуть вверх по скроллу, если он был внизу
    yield put(setSearcherFetchingAction(true)); // финт ушами, чтобы не показать empty results
    yield put(setRoomsAction([])); // очищаем старую неактуальную выдачу
    yield put(startSearchAction());
  }
}

/**
 * Дергает enqueue и получает requestId,
 * по умолчанию на текущую дату
 * @param {Options} options
 */
function* fetchRequestId(
  options: {
    isFlex?: boolean;
    startDate?: string;
    saveRequestId?: boolean;
  } = {},
) {
  const { isFlex = false, startDate } = options;

  const hotel_ids = yield select(getHotelId);
  const start_date = yield select(getSearchParamsStartDate);
  // Хак, для компонента календаря, так как выбрать ту же дату можно
  // только в случае, когда дата это объект(видимо смотри по ссылке)
  const convertedDate = checkDateFormat(start_date)
    ? start_date
    : convertServerDateToClientDate(start_date);
  const carouselDate = yield select(getSearcherCarouselDate);
  const adults = yield select(getSearchParamsAdults);
  const kids = yield select(getSearchParamsKidsCount);
  const kids_ages = yield select(getSearchParamsKidsAgesText);
  const from_city = yield select(getSearchParamsFromCity);
  const from_country = yield select(getSearchParamsFromCountry);
  const to_city = yield select(getSearchParamsToCityEn);
  const to_country = yield select(getSearchParamsToCountry);
  const nights = yield select(getSearchParamsNights);
  const searcherNights = yield select(getSearchedNights);
  const [nightsFrom, nightsTo] = getNightsRange(searcherNights);
  const searchType = yield select(searchTypeSelector);

  const params = {
    hotel_ids,
    from_city,
    from_country,
    to_city,
    to_country,
    nights: isFlex ? `${nightsFrom}..${nightsTo}` : nights,
    adults,
    start_date: carouselDate || startDate || convertedDate,
    kids,
    kids_ages: kids_ages || undefined,
    flex_dates: isFlex,
    search_type: searchType,
  };

  try {
    const { request_id } = yield api.getSearchEnqueue(params);

    return request_id;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
  return null;
}

/**
 * Получает туры или предложения по комнатам
 */
function* fetchRooms(requestId) {
  const obj = {
    rooms: [],
    labels: undefined,
    filters: undefined,
    metapolicies: undefined,
  };

  try {
    const hotelId = yield select(getHotelId);
    const isHotelOnly = yield select(getIsHotelOnly);

    const params = {
      request_id: requestId,
      hotel_id: hotelId,
    };

    const response = isHotelOnly
      ? yield api.getHotelSearchRoomRates(params)
      : yield api.getSearchHotelRooms(params);

    if (!response) {
      throw new Error('Response timed out');
    }

    const { result, labels, meta_policies, filters, error } = response;

    if (!result || error) {
      throw new Error(error || 'No results');
    }

    obj.rooms = result;
    obj.labels = labels;
    obj.metapolicies = meta_policies;
    obj.filters = filters;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error, 'Hotel searcher: Невозможно получить номера в отеле');
  }

  return obj;
}

/**
 * Дергает status, если кто-то из ТО ответил успешно,
 * то получаем туры по комнатам
 */
function* checkStatusAndFetchRooms(requestId) {
  const searchStartTime = Date.now();
  let timeout = false;
  let completed = 0;
  let prevSize = 0;

  while (true) {
    timeout = Date.now() - searchStartTime > SEARCH_TIMEOUT;

    try {
      yield delay(STATUS_INTERVAL);

      const { status, size }: SagaReturnType<typeof api.getSearchStatus> =
        yield api.getSearchStatus({
          request_id: requestId,
        });
      const operatorsStatus = Object.values(status);

      const completedNext = completedOperators(operatorsStatus);

      if (completed !== completedNext || prevSize !== size) {
        const { rooms, labels, metapolicies, filters } = yield fetchRooms(
          requestId,
        );
        const currentRooms = yield select(getRooms);
        const searchCountNights = yield select(getSearchedNights);
        const carouselDate = yield select(getSearcherCarouselDate);
        const isHotelOnly = yield select(getIsHotelOnly);

        if (!isHotelOnly) {
          const nextRooms = mergeRoomsOffers(
            currentRooms,
            rooms,
            searchCountNights,
            convertClientDateToServerDate(carouselDate),
          );
          yield put(updateRoomsAction(nextRooms));
        } else {
          yield put(updateRoomsAction(rooms));
        }

        if (isHotelOnly && filters) {
          yield put(updateHotelFiltersAction(filters));
        }

        const searchOfferFilters = yield select(getSearchOfferFilters);
        const searcherFlters = yield select(getSearcherFilters);
        const searchOfferLabels = yield select(getSearchOfferLabelFilters);
        const existedLabels = yield select(getLabelsFilters);
        const labelsFilter = existedLabels?.paymentBenefits.reduce(
          (acc, curr) => {
            if (searchOfferLabels && searchOfferLabels[curr.id.slice(7)]) {
              return {
                ...acc,
                [curr.id]: true,
              };
            }
            return acc;
          },
          {},
        );
        if (labelsFilter)
          yield put(
            setSearcherFiltersAction({
              ...searchOfferFilters,
              ...searcherFlters,
              ...labelsFilter,
            }),
          );

        if (labels) {
          yield put(setHotelLabels(labels));
        }
        if (metapolicies) {
          yield put(setMetapolicies(metapolicies));
        }
      }

      completed = completedNext;
      prevSize = size;

      if (isSearchRequestDone(operatorsStatus) || timeout) {
        break;
      }
    } catch (error) {
      // TODO: сделать круто
      // eslint-disable-next-line no-console
      console.log('Не удалось проверить статус ТО', error);
      // Если упал метод (например, нет интернета), то ждем 15 секунд и пробуем еще раз
      // По сути костыль, чтобы не умирала вкладка при потере интернета
      // Надо сделать круто либо через serviceWorkers либо как то еще
      yield delay(15000);
    }
  }
}

/**
 * Получить туры
 * - ставит enqueue и получает requestId
 * - запрашивает туры hotel_rooms
 * @typedef Options
 * @property {boolean} isFlex
 * @property {string} startDate
 * @param {Options} options
 */
function* getTours(options?: {
  isFlex?: boolean;
  startDate?: string;
  saveRequestId?: boolean;
}) {
  const requestId = yield call(fetchRequestId, options);

  if (get(options, 'saveRequestId')) {
    yield put(updateRequestIdAction(requestId));
  }

  yield call(checkStatusAndFetchRooms, requestId);
}

/**
 * Управляет процессом поиска туров
 * - устанавливаем обработчики для событий STOP_SEARCH
 * - запускаем получение requestId
 */
function* performSearchPackage() {
  yield takeLatest(STOP_SEARCH, restartSearcher);

  yield all([
    call(getTours, { isFlex: true, saveRequestId: true }),
    call(getTours),
  ]);

  yield put(stopSearchAction());
}

/**
 * Управляет процессом поиска отелей
 * - устанавливаем обработчики для событий STOP_SEARCH
 * - запускаем получение requestId
 */
function* performSearchHotel() {
  yield takeLatest(STOP_SEARCH, restartSearcher);

  yield all([call(getTours, { saveRequestId: true }), call(getTours)]);

  yield put(stopSearchAction());
}

function* handleStartSearch() {
  const isHotelOnly = yield select(getIsHotelOnly);
  const performSearch = isHotelOnly ? performSearchHotel : performSearchPackage;
  yield performSearch();
}

/**
 * Собирает поисковые параметры из поисковика
 */
function* collectSearchParams() {
  const data = yield select(getSearcherData);
  const { startDate } = data;
  data.startDate = checkDateFormat(startDate)
    ? startDate
    : convertServerDateToClientDate(data.startDate);

  const searchType = yield select(getSearchType);
  const params = pick(data, [
    'adults',
    'kidsAges',
    'nights',
    'to',
    'startDate',
    'from',
  ]);
  const convertStartDate = convertClientDateToServerDate(params.startDate);
  const endDate = addDays(new Date(convertStartDate), params.nights);

  return yield {
    nights: { from: params.nights, to: params.nights },
    to: params.to,
    adults: params.adults,
    start_date: convertStartDate,
    end_date: endDate,
    start_date_flex: 0,
    end_date_flex: 0,
    search_type: searchType,
    kids: { ages: params.kidsAges, count: params.kidsAges.length },
    from: params.from,
  };
}

/**
 * Собирает изменения с сигареты
 */
function* collectSearcherParams(params) {
  const data = yield select(getSearcherData);
  const isHotelOnly = yield select(getIsHotelOnly);
  const anyDeparture = {
    name_ru: 'Любой город',
    id: 0,
    name_en: 'Any',
  };

  const departure = isHotelOnly
    ? anyDeparture
    : yield setDeparture(params.from_city);

  return yield {
    ...data,
    adults: params.adults,
    kidsAges: params.kids_ages ? params.kids_ages.split(',') : [],
    startDate: params.start_date,
    carouselDate: params.start_date,
    nights: params.nights,
    from: {
      city: departure,
      country: { iso2: params.from_country },
    },
    departure,
  };
}

/**
 * Назначение города вылета
 */
function* setDeparture(nameEn) {
  const departuresList = yield select(getSearcherDepartures);

  const options = departuresList.filter((dep) =>
    dep.name_en.toLocaleLowerCase().includes(nameEn.toLocaleLowerCase()),
  );
  if (!options.length) {
    return options.unshift({ id: -1, name_ru: 'Такой город не найден' });
  }
  return options[0];
}

/**
 * Коммитит изменения и стартует поиск
 */
function* commitAndStartSearch() {
  const searchParams = yield collectSearchParams();

  yield put(commitSearchParamsAction(searchParams));
  yield put(startSearchAction());
  yield put(setRoomsAction([]));
}

/**
 * Обрабатывает событие Узнать цену в карусели дат
 */
function* handleSearchPriceForDate({
  payload,
}: ReturnType<typeof searchPriceForDateAction>) {
  const searchOffer = yield select(getSearchOfferFrom);
  const date = convertClientDateToServerDate(payload);
  yield put(setSearcherDateAction(convertServerDateToClientDate(date)));
  yield put(setSearcherCarouselDateAction({ date: payload }));

  yield commitAndStartSearch();
  yield put(setFromSearchOfferAction(searchOffer));
}

type DepartureType = {
  id: number;
  label: string;
  value: string;
  iso2: string;
};

/**
 * Изменение города отправления и запускает поиск
 */
function* handleSearcherDepartureParams(searchType) {
  let searchOffer = yield select(getSearchOfferFrom);
  if (searchType === 'hotel') {
    yield put(
      setDepartureAction({
        id: 0,
        name_ru: 'Любой город',
        name_en: 'Any',
        iso2: 'RU',
      }),
    );
  } else {
    const { id, name_ru, name_en, iso2 } = searchOffer;
    // Если город вылета не определен
    if (id === 0) {
      const departureJS = localStorage?.getItem('lt-departure-city');
      const departureForIncognito: DepartureType = {
        id: 213,
        label: 'Москва',
        value: 'Moscow',
        iso2: 'RU',
      };

      const parsedDeparture = parseJSON<DepartureType>(departureJS);

      // Если нет id, то берет дефолтные данные
      const departure =
        departureJS && parsedDeparture?.id
          ? parsedDeparture
          : departureForIncognito;

      yield put(
        setDepartureAction({
          id: departure.id,
          name_ru: departure.label,
          name_en: departure.value,
          iso2: departure.iso2,
        }),
      );
    } else {
      yield put(
        setDepartureAction({
          id,
          name_ru,
          name_en,
          iso2,
        }),
      );
    }
    searchOffer = yield select(getSearcherDeparture);
  }
  yield commitAndStartSearch();
  yield put(setFromSearchOfferAction(searchOffer));
}

/**
 * При переключении табов запускает поиск
 */
function* handleSetSearchType(action) {
  const { payload } = action;
  const uri = URI();

  yield put(setSearcherFetchingAction(false));

  yield handleSearcherDepartureParams(payload);
  yield put(clearSearcherFiltersAction());
  yield put(clearSearcherFiltersActionInSetTab());
  yield put(setSearcherDefaultFiltersAction(false));
  uri.removeSearch([
    'offer_meals',
    'offer_labels',
    'offer_free_cancel',
    'offer_instant_confirm',
    'offer_operators',
    'offer_payment_benefits',
  ]);
  const clientUrl = URI.decode(uri.toString());
  window.history.replaceState(null, document.title, clientUrl);
}

/**
 * Запускает поиск сигареты
 */
function* setSearcher(action) {
  const searcherParams = yield collectSearcherParams(action.payload);
  const searchOffer = yield select(getSearchOfferFrom);

  yield put(updateSearcherParamsAction(searcherParams));

  yield call(commitAndStartSearch);
  yield put(setFromSearchOfferAction(searchOffer));
}

/**
 * Устанавливает в форму Москву и запускает поиск
 */
function* handleSearchWithDefaultDeparture() {
  yield put(
    setDepartureAction({
      id: MOSCOW_CITY_ID,
      name_ru: 'Москва',
      name_en: 'Moscow',
    }),
  );

  const searchParamsUrl = new URLSearchParams(window.location.search);
  searchParamsUrl.set('search_type', 'package');
  searchParamsUrl.set('from', 'Moscow-RU');
  window.location.search = searchParamsUrl.toString();
}

function* handleSetSearcherCarouselDate(action) {
  const {
    payload: { date, loadData },
  } = action;
  const startDate = yield select(getSearchParamsStartDate);

  if (loadData && isDateBetweenRange({ centerDate: startDate, date })) {
    yield delay(1000);
    yield put(setSearcherFetchingAction(true));
    yield call(getTours, { startDate: date });
    yield put(setSearcherFetchingAction(false));
  } else {
    yield put(setSearcherFetchingAction(false));
  }
}

function* handleSetFilters() {
  const uri = URI();
  const defaultFilters = yield select(getIsSetDefaultFilters);
  const operators = yield select(getSearcherOperatorsFilter);
  const freeCancel = yield select(getSearcherFreeCancel);
  const instantConfirm = yield select(getSearcherInstantConfirm);
  const filters = yield select(getSearcherFilters);
  const entriesFilters = Object.entries(filters);

  const mealsPrefix = 'lt_meals_';
  const labelsPrefix = 'labels_';
  const mealsFilters = entriesFilters
    .filter(
      ([filterName, filterValue]) =>
        filterName.startsWith(mealsPrefix) && filterValue === true,
    )
    .map(([filterName]) => filterName.slice(mealsPrefix.length))
    .join();
  const labelsFilters = entriesFilters
    .filter(
      ([filterName, filterValue]) =>
        filterName.startsWith(labelsPrefix) && filterValue === true,
    )
    .map(([filterName]) => filterName.slice(labelsPrefix.length))
    .join();
  const entriesOperators = Object.entries(operators);
  const activeOperators = entriesOperators
    .filter(
      ([filterName, filterValue]) =>
        !Number.isNaN(filterName) && filterValue === true,
    )
    .map(([filterName]) => filterName)
    .join();
  uri.removeSearch([
    'offer_meals',
    'offer_labels',
    'offer_free_cancel',
    'offer_instant_confirm',
    'offer_operators',
    'offer_payment_benefits',
  ]);
  if (instantConfirm) {
    uri.addSearch('offer_instant_confirm', true);
  }
  if (freeCancel) {
    uri.addSearch('offer_free_cancel', true);
  }
  if (mealsFilters) {
    uri.addSearch('offer_meals', mealsFilters);
  }
  if (labelsFilters) {
    uri.addSearch('offer_payment_benefits', labelsFilters);
  }
  if (activeOperators) {
    uri.addSearch('offer_operators', activeOperators);
  }
  const clientUrl = URI.decode(uri.toString());
  window.history.replaceState(null, document.title, clientUrl);
  if (defaultFilters) {
    const filtersValues = Object.values(filters);
    const isFiltersOff = filtersValues.every((element) => element === false);
    if (isFiltersOff && !freeCancel && !instantConfirm) {
      yield put(setSearcherDefaultFiltersAction(false));
    }
  }
}

// Применяем фильтр по операторам, если он был выбран на странице поиска
export function* handleApplyOperatorFilters() {
  const searchOfferOperators = yield select(getSearchOfferOperators);

  if (isEmpty(searchOfferOperators)) {
    return;
  }

  const searchOfferFilters = yield select(getSearchOfferFilters); // instantConfirm и freeCancel
  const appliedFilters = yield select(getSearcherFilters);
  const appliedOperatorsFilter = yield select(getSearcherOperatorsFilter);
  const existedOperators = yield select(getRoomsOperators);

  const operatorsFilter = existedOperators.reduce((acc, curr) => {
    if (searchOfferOperators[curr.id]) {
      return {
        ...acc,
        [curr.id]: true,
      };
    }
    return acc;
  }, {});

  // Если не пришел новый ТО, то не обновляем фильтры
  if (isEqual(operatorsFilter, appliedOperatorsFilter)) {
    return;
  }

  // Показываем плашку про то, что часть туров отфильтрована
  yield put(setSearcherDefaultFiltersAction(true));
  yield put(
    setSearcherFiltersAction({
      ...searchOfferFilters,
      ...appliedFilters,
      ...operatorsFilter,
    }),
  );
}

export default function* searcherSaga() {
  yield all([
    takeLatest(INIT_APP, handleInitApp),
    takeLatest([START_SEARCH, logInAction.type], handleStartSearch),
    takeLatest(searchPriceForDateAction.type, handleSearchPriceForDate),
    takeLatest(SEARCH_WITH_DEFAULT_DEPARTURE, handleSearchWithDefaultDeparture),
    takeLatest(SET_SEARCHER_CAROUSEL_DATE, handleSetSearcherCarouselDate),
    takeLatest(SET_SEARCH_TYPE, handleSetSearchType),
    takeLatest(SET_SEARCHER_PARAMS, setSearcher),
    takeLatest(SET_FILTERS, handleSetFilters),
    takeLatest(CLEAR_SEARCHER_FILTERS, handleSetFilters),
    takeLatest([SET_ROOMS, UPDATE_ROOMS], handleApplyOperatorFilters),
  ]);
}
